import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import { getReport, getAbsences, logAbsence, deleteAbsence, exportData, getMembers, getEscalations, acknowledgeEscalation, getQuarterlyReport, checkEscalations } from './businessApi';

function monthRange() {
  const now = new Date();
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const to   = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${last}`;
  return { from, to };
}

export default function BusinessReportsScreen({ lang, businessId, isOwner }) {
  const nl = lang === 'nl';
  const [tab, setTab] = useState('report');

  // Report
  const [report, setReport]       = useState(null);
  const [loadingRep, setLoadingRep] = useState(true);

  // Absences
  const [absences, setAbsences]   = useState([]);
  const [members, setMembers]     = useState([]);
  const [loadingAbs, setLoadingAbs] = useState(true);
  const [range, setRange]         = useState(monthRange);
  const [newAbs, setNewAbs]       = useState({ user_id: '', absence_date: new Date().toISOString().split('T')[0], reason: '' });
  const [addingAbs, setAddingAbs] = useState(false);

  // Escalations
  const [escalations, setEscalations] = useState([]);
  const [loadingEsc, setLoadingEsc]   = useState(false);

  // Quarterly report
  const [quarterly, setQuarterly]     = useState(null);
  const [loadingQtr, setLoadingQtr]   = useState(false);

  const loadReport = () => {
    setLoadingRep(true);
    getReport(businessId).then(d => { setReport(d); setLoadingRep(false); }).catch(() => setLoadingRep(false));
  };
  const loadAbsences = () => {
    setLoadingAbs(true);
    getAbsences(businessId, range.from, range.to).then(d => { setAbsences(Array.isArray(d) ? d : []); setLoadingAbs(false); }).catch(() => setLoadingAbs(false));
  };
  const loadMembers = () => {
    getMembers(businessId).then(d => setMembers(Array.isArray(d) ? d.filter(m => m.status === 'active') : [])).catch(() => {});
  };

  const loadEscalations = () => {
    setLoadingEsc(true);
    getEscalations(businessId).then(d => { setEscalations(Array.isArray(d) ? d : []); setLoadingEsc(false); }).catch(() => setLoadingEsc(false));
  };

  const loadQuarterly = () => {
    setLoadingQtr(true);
    getQuarterlyReport(businessId).then(d => { setQuarterly(d); setLoadingQtr(false); }).catch(() => setLoadingQtr(false));
  };

  const handleAcknowledge = async (eid) => {
    await acknowledgeEscalation(businessId, eid);
    loadEscalations();
  };

  const handleCheckEscalations = async () => {
    await checkEscalations(businessId);
    loadEscalations();
  };

  useEffect(() => { loadReport(); loadMembers(); }, [businessId]); // eslint-disable-line
  useEffect(() => { if (tab === 'absences') loadAbsences(); }, [tab, range]); // eslint-disable-line
  useEffect(() => { if (tab === 'escalations') loadEscalations(); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === 'quarterly') loadQuarterly(); }, [tab]); // eslint-disable-line

  const handleLogAbsence = async () => {
    if (!newAbs.user_id || !newAbs.absence_date) return;
    setAddingAbs(true);
    await logAbsence(businessId, newAbs);
    setNewAbs({ user_id: '', absence_date: new Date().toISOString().split('T')[0], reason: '' });
    setAddingAbs(false);
    loadAbsences();
  };

  const handleDeleteAbsence = async (aid) => {
    await deleteAbsence(businessId, aid);
    loadAbsences();
  };

  // Feature 7: Burnout prevention plan for a member
  const burnoutPlan = (m, cfg) => {
    const tips = [];
    if (m.avg_sleep !== null && m.avg_sleep < 6.5) {
      tips.push(nl ? '😴 Stimuleer een vaste bedtijd en beperk schermtijd na 21:00.' : '😴 Encourage a consistent bedtime and limit screens after 9pm.');
      tips.push(nl ? '🛌 Korte dutjes van 20 min. kunnen alertheid herstellen.' : '🛌 Short 20-min naps can restore alertness.');
    }
    if (m.avg_mood !== null && m.avg_mood < 3.5) {
      tips.push(nl ? '🧘 Plan elke ochtend 5 minuten bewuste ademhaling in.' : '🧘 Schedule 5 minutes of mindful breathing each morning.');
      tips.push(nl ? '💬 Bied een vertrouwelijk gesprek aan met een manager of coach.' : '💬 Offer a confidential conversation with a manager or coach.');
    }
    tips.push(nl ? '🚶 Dagelijkse wandeling van 15 minuten vermindert stress significant.' : '🚶 A daily 15-minute walk significantly reduces stress levels.');
    if (tips.length === 0) tips.push(nl ? '✅ Dit teamlid vertoont geen risicosignalen.' : '✅ This team member shows no risk signals.');
    return tips;
  };

  // Summary for report copy
  const buildReportText = () => {
    if (!report) return '';
    const ms = report.members || [];
    const avgSleepAll = ms.filter(m => m.avg_sleep != null).reduce((a, m) => a + m.avg_sleep, 0) / (ms.filter(m => m.avg_sleep != null).length || 1);
    const avgMoodAll  = ms.filter(m => m.avg_mood != null).reduce((a, m) => a + m.avg_mood,  0) / (ms.filter(m => m.avg_mood  != null).length || 1);
    let lines = [
      `📊 ${nl ? 'Wekelijks Wellness Rapport' : 'Weekly Wellness Report'} — ${report.week_start}`,
      `👥 ${nl ? 'Teamleden' : 'Team members'}: ${ms.length}`,
      `😴 ${nl ? 'Gem. slaap' : 'Avg sleep'}: ${avgSleepAll.toFixed(1)}h`,
      `😊 ${nl ? 'Gem. humeur' : 'Avg mood'}: ${avgMoodAll.toFixed(1)}/5`,
      `🏥 ${nl ? 'Ziekmeldingen' : 'Absences'}: ${report.absences_this_week || 0}`,
      '',
      ...(ms.map(m => `• ${m.name}: ${nl ? 'Slaap' : 'Sleep'} ${m.avg_sleep != null ? m.avg_sleep.toFixed(1) : '—'}h, ${nl ? 'Humeur' : 'Mood'} ${m.avg_mood != null ? m.avg_mood.toFixed(1) : '—'}/5`)),
    ];
    return lines.join('\n');
  };

  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard?.writeText(buildReportText()).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Sub-tabs */}
      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
        <div style={{ display: 'flex', background: _T.bg4, borderRadius: 14, padding: 4, gap: 4, minWidth: 'max-content' }}>
          {[
            ['report', nl ? '📋 Rapport' : '📋 Report'],
            ['absences', nl ? '🏥 Verzuim' : '🏥 Absences'],
            ['burnout', nl ? '🔥 Burnout' : '🔥 Burnout'],
            ['escalations', nl ? '🚨 Escalaties' : '🚨 Escalations'],
            ['quarterly', nl ? '📊 Kwartaal' : '📊 Quarterly'],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ background: tab === id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : 'transparent', border: 'none', borderRadius: 10, color: tab === id ? '#0a1220' : C.silver, fontSize: 11, fontWeight: 700, padding: '9px 12px', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Feature 1: Weekly Wellness Report */}
      {tab === 'report' && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'WEKELIJKS WELLNESS RAPPORT' : 'WEEKLY WELLNESS REPORT'}</div>
          {loadingRep ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>...</div>
          ) : !report ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 32 }}>{nl ? 'Rapport laden mislukt.' : 'Failed to load report.'}</div>
          ) : (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
                {[
                  { icon: '👥', label: nl ? 'Leden' : 'Members', val: report.members?.length || 0, color: _T.accent },
                  { icon: '😴', label: nl ? 'Gem. slaap' : 'Avg sleep', val: (() => { const ms = (report.members||[]).filter(m => m.avg_sleep!=null); return ms.length ? (ms.reduce((a,m)=>a+m.avg_sleep,0)/ms.length).toFixed(1)+'h' : '—'; })(), color: _T.accent },
                  { icon: '🏥', label: nl ? 'Ziekmeldingen' : 'Absences', val: report.absences_this_week || 0, color: C.orange },
                ].map(s => (
                  <div key={s.label} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '12px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 18 }}>{s.icon}</div>
                    <div style={{ color: s.color, fontSize: 18, fontWeight: 800 }}>{s.val}</div>
                    <div style={{ color: C.silver, fontSize: 9 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Per-member table */}
              <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, overflow: 'hidden', marginBottom: 14 }}>
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${_T.cardBorderBot}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 60px', gap: 8 }}>
                    <span style={{ color: C.silver, fontSize: 10 }}>{nl ? 'NAAM' : 'NAME'}</span>
                    <span style={{ color: C.silver, fontSize: 10, textAlign: 'center' }}>{nl ? 'SLAAP' : 'SLEEP'}</span>
                    <span style={{ color: C.silver, fontSize: 10, textAlign: 'center' }}>{nl ? 'HUMEUR' : 'MOOD'}</span>
                    <span style={{ color: C.silver, fontSize: 10, textAlign: 'center' }}>CHECK-INS</span>
                  </div>
                </div>
                {(report.members || []).map(m => (
                  <div key={m.name} style={{ padding: '10px 14px', borderBottom: `1px solid ${_T.cardBorderBot}10` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 60px', gap: 8, alignItems: 'center' }}>
                      <span style={{ color: C.white, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                      <span style={{ color: m.avg_sleep != null && m.avg_sleep < 6 ? C.red : m.avg_sleep != null && m.avg_sleep < 7 ? C.orange : _T.accent, fontSize: 13, fontWeight: 700, textAlign: 'center' }}>{m.avg_sleep != null ? m.avg_sleep.toFixed(1) : '—'}</span>
                      <span style={{ color: m.avg_mood != null && m.avg_mood < 3 ? C.red : m.avg_mood != null && m.avg_mood < 3.5 ? C.orange : C.teal, fontSize: 13, fontWeight: 700, textAlign: 'center' }}>{m.avg_mood != null ? m.avg_mood.toFixed(1) : '—'}</span>
                      <span style={{ color: C.silver, fontSize: 12, textAlign: 'center' }}>{m.check_ins || 0}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleCopy}
                  style={{ flex: 1, background: copied ? `${C.teal}20` : _T.bg3, border: `1px solid ${copied ? C.teal : _T.cardBorderBot}`, borderRadius: 12, color: copied ? C.teal : C.silver, fontSize: 13, fontWeight: 600, padding: '12px', cursor: 'pointer', transition: 'all 0.2s' }}>
                  {copied ? (nl ? '✓ Gekopieerd!' : '✓ Copied!') : (nl ? '📋 Kopieer rapport' : '📋 Copy report')}
                </button>
                <a href={exportData(businessId)} target="_blank" rel="noreferrer"
                  style={{ flex: 1, background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, borderRadius: 12, color: _T.accent, fontSize: 13, fontWeight: 700, padding: '12px', cursor: 'pointer', textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {nl ? '⬇ CSV Export' : '⬇ CSV Export'}
                </a>
              </div>
            </>
          )}
        </>
      )}

      {/* Feature 11: Absenteeism */}
      {tab === 'absences' && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'VERZUIM REGISTRATIE' : 'ABSENCE TRACKING'}</div>

          {/* Date range filter */}
          <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '12px 14px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: C.silver, marginBottom: 4 }}>{nl ? 'Van' : 'From'}</div>
              <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: C.silver, marginBottom: 4 }}>{nl ? 'Tot' : 'To'}</div>
              <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12 }} />
            </div>
          </div>

          {/* Log new absence (owner only) */}
          {isOwner && (
            <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: C.silver, marginBottom: 10 }}>{nl ? 'Ziekmelding registreren' : 'Log absence'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <select value={newAbs.user_id} onChange={e => setNewAbs(p => ({ ...p, user_id: e.target.value }))}
                  style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 13 }}>
                  <option value="">{nl ? '— Selecteer teamlid —' : '— Select team member —'}</option>
                  {members.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: C.silver, marginBottom: 4 }}>{nl ? 'Datum' : 'Date'}</div>
                    <input type="date" value={newAbs.absence_date} onChange={e => setNewAbs(p => ({ ...p, absence_date: e.target.value }))} style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: C.silver, marginBottom: 4 }}>{nl ? 'Reden (optioneel)' : 'Reason (optional)'}</div>
                    <input value={newAbs.reason} onChange={e => setNewAbs(p => ({ ...p, reason: e.target.value }))}
                      placeholder={nl ? 'Bijv. ziek' : 'E.g. sick'}
                      style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12 }} />
                  </div>
                </div>
                <button onClick={handleLogAbsence} disabled={addingAbs || !newAbs.user_id}
                  style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '10px', cursor: (addingAbs || !newAbs.user_id) ? 'default' : 'pointer' }}>
                  {addingAbs ? '...' : (nl ? '+ Registreer' : '+ Log absence')}
                </button>
              </div>
            </div>
          )}

          {/* Absence log */}
          {loadingAbs ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>...</div>
          ) : absences.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.silver, fontSize: 13 }}>
              {nl ? 'Geen ziekmeldingen in deze periode.' : 'No absences in this period.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Correlation summary */}
              {(() => {
                const byMember = absences.reduce((acc, a) => { acc[a.user_id] = (acc[a.user_id] || 0) + 1; return acc; }, {});
                const topAbsent = Object.entries(byMember).sort((a, b) => b[1] - a[1]).slice(0, 3);
                if (topAbsent.length === 0) return null;
                return (
                  <div style={{ background: `${C.orange}10`, border: `1px solid ${C.orange}33`, borderRadius: 14, padding: '12px 14px', marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, marginBottom: 8 }}>📊 {nl ? 'VERZUIM CORRELATIE' : 'ABSENCE CORRELATION'}</div>
                    {topAbsent.map(([uid, cnt]) => {
                      const memb = absences.find(a => a.user_id === uid);
                      return (
                        <div key={uid} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: C.silver, fontSize: 12 }}>{memb?.name || uid}</span>
                          <span style={{ color: C.orange, fontSize: 12, fontWeight: 700 }}>{cnt} {nl ? 'dag(en)' : 'day(s)'}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {absences.map(a => (
                <div key={a.id} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{a.name || a.user_id}</div>
                    <div style={{ color: C.silver, fontSize: 11 }}>{a.absence_date}{a.reason ? ` — ${a.reason}` : ''}</div>
                  </div>
                  {isOwner && (
                    <button onClick={() => handleDeleteAbsence(a.id)}
                      style={{ background: 'none', border: 'none', color: `${C.red}66`, cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Escalations tab */}
      {tab === 'escalations' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700 }}>{nl ? 'AUTO ESCALATIE LOG' : 'AUTO ESCALATION LOG'}</div>
            <button onClick={handleCheckEscalations}
              style={{ background: `${C.red}15`, border: `1px solid ${C.red}44`, borderRadius: 10, color: C.red, fontSize: 11, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}>
              {nl ? '🔄 Check nu' : '🔄 Check now'}
            </button>
          </div>
          <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
            {nl
              ? 'Teamleden die 2+ weken achter elkaar hoog risico tonen worden automatisch geëscaleerd.'
              : 'Team members showing high risk for 2+ consecutive weeks are auto-escalated here.'}
          </div>
          {loadingEsc ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>...</div>
          ) : escalations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: C.silver }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 13 }}>{nl ? 'Geen escalaties — goed bezig!' : 'No escalations — great job!'}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {escalations.map(e => (
                <div key={e.id} style={{ background: e.acknowledged ? _T.bg3 : `${C.red}08`, border: `1px solid ${e.acknowledged ? _T.cardBorderBot : C.red + '44'}`, borderRadius: 14, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: e.acknowledged ? C.silver : C.white, fontSize: 13, fontWeight: 700 }}>{e.name || e.user_id}</span>
                        <span style={{ fontSize: 9, color: C.red, background: `${C.red}15`, padding: '2px 7px', borderRadius: 6, fontWeight: 700 }}>
                          {e.risk_level?.toUpperCase()} — {e.weeks_high}w
                        </span>
                        {e.acknowledged === 1 && <span style={{ fontSize: 9, color: C.teal, background: `${C.teal}15`, padding: '2px 7px', borderRadius: 6 }}>✓ {nl ? 'Erkend' : 'Acknowledged'}</span>}
                      </div>
                      <div style={{ color: C.silver, fontSize: 11, marginTop: 4 }}>
                        {nl ? `${e.weeks_high} weken hoog risico · Gemeld op ` : `${e.weeks_high} weeks high risk · Flagged `}
                        {new Date(e.notified_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB')}
                      </div>
                    </div>
                    {!e.acknowledged && (
                      <button onClick={() => handleAcknowledge(e.id)}
                        style={{ background: `${C.teal}15`, border: `1px solid ${C.teal}44`, borderRadius: 8, color: C.teal, fontSize: 11, fontWeight: 600, padding: '6px 10px', cursor: 'pointer', flexShrink: 0 }}>
                        {nl ? 'Erken' : 'Acknowledge'}
                      </button>
                    )}
                  </div>
                  {!e.acknowledged && (
                    <div style={{ marginTop: 10, background: `${C.orange}10`, border: `1px solid ${C.orange}33`, borderRadius: 8, padding: '8px 10px', fontSize: 11, color: C.orange }}>
                      💡 {nl ? 'Aanbeveling: Plan een vertrouwelijk gesprek met dit teamlid deze week.' : 'Recommendation: Schedule a confidential conversation with this team member this week.'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Quarterly Board Report */}
      {tab === 'quarterly' && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'KWARTAALRAPPORT BESTUUR' : 'QUARTERLY BOARD REPORT'}</div>
          {loadingQtr ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>...</div>
          ) : !quarterly ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.silver }}>{nl ? 'Laden mislukt.' : 'Failed to load.'}</div>
          ) : (
            <>
              {/* Quarter header */}
              <div style={{ background: `linear-gradient(135deg,${_T.grad1}22,${_T.bg3})`, border: `1px solid ${_T.accent}33`, borderRadius: 16, padding: '16px', marginBottom: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 2, marginBottom: 4 }}>{quarterly.quarter}</div>
                <div style={{ color: C.white, fontSize: 20, fontWeight: 800 }}>{nl ? 'Team Welzijnsrapport' : 'Team Wellness Report'}</div>
                <div style={{ color: C.silver, fontSize: 12, marginTop: 4 }}>{quarterly.member_count} {nl ? 'teamleden · 90 dagen' : 'team members · 90 days'}</div>
              </div>

              {/* KPI grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 14 }}>
                {[
                  { icon: '😴', label: nl ? 'Gem. slaap' : 'Avg sleep', val: quarterly.avg_sleep != null ? quarterly.avg_sleep.toFixed(1) + 'h' : '—', color: _T.accent },
                  { icon: '😊', label: nl ? 'Gem. humeur' : 'Avg mood', val: quarterly.avg_mood != null ? quarterly.avg_mood.toFixed(1) + '/5' : '—', color: C.teal },
                  { icon: '🚨', label: nl ? 'Hoog risico' : 'High risk', val: quarterly.high_risk, color: quarterly.high_risk > 0 ? C.red : C.teal },
                  { icon: '⚠️', label: nl ? 'Verhoogd risico' : 'Medium risk', val: quarterly.medium_risk, color: quarterly.medium_risk > 0 ? C.orange : C.teal },
                  { icon: '🏥', label: nl ? 'Verzuim (kwartaal)' : 'Absences (quarter)', val: quarterly.total_absences, color: C.orange },
                  { icon: '👥', label: nl ? 'Teamleden' : 'Members', val: quarterly.member_count, color: _T.accent },
                ].map((kpi, i) => (
                  <div key={i} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20 }}>{kpi.icon}</div>
                    <div style={{ color: kpi.color, fontSize: 20, fontWeight: 800, marginTop: 2 }}>{kpi.val}</div>
                    <div style={{ color: C.silver, fontSize: 10, marginTop: 2 }}>{kpi.label}</div>
                  </div>
                ))}
              </div>

              {/* Risk distribution */}
              {quarterly.members?.length > 0 && (
                <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 12 }}>{nl ? 'RISICODISTRIBUTIE' : 'RISK DISTRIBUTION'}</div>
                  {['high', 'medium', 'low'].map(level => {
                    const count = quarterly.members.filter(m => m.burnout_risk === level).length;
                    const pct = Math.round((count / quarterly.member_count) * 100);
                    const color = level === 'high' ? C.red : level === 'medium' ? C.orange : C.teal;
                    const label = level === 'high' ? (nl ? 'Hoog' : 'High') : level === 'medium' ? (nl ? 'Middel' : 'Medium') : (nl ? 'Laag' : 'Low');
                    return (
                      <div key={level} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                          <span style={{ color }}>{label} risk</span>
                          <span style={{ color: C.silver }}>{count} ({pct}%)</span>
                        </div>
                        <div style={{ background: `${color}15`, borderRadius: 4, height: 8 }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Print/export */}
              <button onClick={() => window.print()}
                style={{ width: '100%', background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, borderRadius: 14, color: _T.accent, fontSize: 14, fontWeight: 700, padding: '13px', cursor: 'pointer' }}>
                🖨️ {nl ? 'Rapport afdrukken / opslaan als PDF' : 'Print / Save as PDF'}
              </button>

              {/* Full member list */}
              {quarterly.members?.length > 0 && (
                <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, overflow: 'hidden', marginTop: 14 }}>
                  <div style={{ padding: '10px 14px', borderBottom: `1px solid ${_T.cardBorderBot}`, display: 'grid', gridTemplateColumns: '1fr 60px 60px 80px', gap: 8 }}>
                    <span style={{ color: C.silver, fontSize: 10 }}>{nl ? 'NAAM' : 'NAME'}</span>
                    <span style={{ color: C.silver, fontSize: 10, textAlign: 'center' }}>😴</span>
                    <span style={{ color: C.silver, fontSize: 10, textAlign: 'center' }}>😊</span>
                    <span style={{ color: C.silver, fontSize: 10, textAlign: 'center' }}>{nl ? 'RISICO' : 'RISK'}</span>
                  </div>
                  {quarterly.members.map(m => {
                    const rColor = m.burnout_risk === 'high' ? C.red : m.burnout_risk === 'medium' ? C.orange : C.teal;
                    return (
                      <div key={m.name} style={{ padding: '9px 14px', borderBottom: `1px solid ${_T.cardBorderBot}10`, display: 'grid', gridTemplateColumns: '1fr 60px 60px 80px', gap: 8, alignItems: 'center' }}>
                        <span style={{ color: C.white, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                        <span style={{ color: _T.accent, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>{m.avg_sleep != null ? m.avg_sleep.toFixed(1) : '—'}</span>
                        <span style={{ color: C.teal, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>{m.avg_mood != null ? m.avg_mood.toFixed(1) : '—'}</span>
                        <span style={{ fontSize: 9, color: rColor, background: `${rColor}15`, padding: '2px 6px', borderRadius: 6, fontWeight: 700, textAlign: 'center' }}>{m.burnout_risk?.toUpperCase()}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Feature 7: Burnout Prevention Plan */}
      {tab === 'burnout' && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'BURNOUT PREVENTIE PLAN' : 'BURNOUT PREVENTION PLAN'}</div>
          {loadingRep ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>...</div>
          ) : !report || !report.members?.length ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.silver, fontSize: 13 }}>
              {nl ? 'Geen data beschikbaar.' : 'No data available.'}
            </div>
          ) : (
            <>
              {report.members.filter(m => m.avg_sleep != null || m.avg_mood != null).sort((a, b) => {
                const risk = m => {
                  if ((m.avg_sleep != null && m.avg_sleep < 5) || (m.avg_mood != null && m.avg_mood < 2.5)) return 0;
                  if ((m.avg_sleep != null && m.avg_sleep < 6.5) || (m.avg_mood != null && m.avg_mood < 3.5)) return 1;
                  return 2;
                };
                return risk(a) - risk(b);
              }).map(m => {
                const highRisk = (m.avg_sleep != null && m.avg_sleep < 5) || (m.avg_mood != null && m.avg_mood < 2.5);
                const medRisk  = !highRisk && ((m.avg_sleep != null && m.avg_sleep < 6.5) || (m.avg_mood != null && m.avg_mood < 3.5));
                const rColor = highRisk ? C.red : medRisk ? C.orange : C.teal;
                const rLabel = highRisk ? (nl ? 'HOOG RISICO' : 'HIGH RISK') : medRisk ? (nl ? 'VERHOOGD' : 'ELEVATED') : (nl ? 'LAAG' : 'LOW');
                const tips = burnoutPlan(m);
                return (
                  <div key={m.name} style={{ background: _T.bg3, border: `1px solid ${rColor}33`, borderRadius: 16, padding: '14px 16px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{m.name}</div>
                      <span style={{ fontSize: 9, color: rColor, background: `${rColor}15`, border: `1px solid ${rColor}33`, borderRadius: 8, padding: '3px 8px', fontWeight: 700 }}>{rLabel}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: C.silver, fontSize: 10 }}>😴 {nl ? 'Slaap' : 'Sleep'}</div>
                        <div style={{ color: m.avg_sleep != null && m.avg_sleep < 6.5 ? C.red : _T.accent, fontSize: 16, fontWeight: 700 }}>{m.avg_sleep != null ? m.avg_sleep.toFixed(1) : '—'}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: C.silver, fontSize: 10 }}>😊 {nl ? 'Humeur' : 'Mood'}</div>
                        <div style={{ color: m.avg_mood != null && m.avg_mood < 3.5 ? C.red : C.teal, fontSize: 16, fontWeight: 700 }}>{m.avg_mood != null ? m.avg_mood.toFixed(1) : '—'}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: C.silver, marginBottom: 6, letterSpacing: 1 }}>{nl ? 'AANBEVOLEN ACTIES' : 'RECOMMENDED ACTIONS'}</div>
                    {tips.map((tip, i) => (
                      <div key={i} style={{ background: `${rColor}08`, borderLeft: `3px solid ${rColor}55`, borderRadius: 6, padding: '8px 10px', marginBottom: 6, color: C.silver, fontSize: 12, lineHeight: 1.5 }}>
                        {tip}
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </>
      )}
    </div>
  );
}
